import React from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Bell, Eye, CheckCircle2, AlarmClock } from "lucide-react";
import { API } from "@/lib/api";
import { fmtDateTime } from "@/lib/quote-helpers";

export const NotificationsBell = ({
  notifications,
  unread,
  markAllRead,
  fetchNotifications,
  loadOne,
  setTab,
}) => {
  return (
    <Popover onOpenChange={(o) => { if (o) markAllRead(); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative border-[var(--brand-border)] text-[var(--brand-ink)] hover:bg-[var(--brand-border-light)]"
          data-testid="notifications-btn"
          onClick={fetchNotifications}
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-[var(--brand-primary)] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
              data-testid="notif-badge"
            >
              {unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 bg-white border-[var(--brand-border)]"
        data-testid="notif-popover"
      >
        <div className="px-4 py-3 border-b border-[var(--brand-border)] flex items-center justify-between">
          <div>
            <div className="font-[Playfair_Display] font-semibold text-[var(--brand-ink)]">
              Angebots-Aufrufe
            </div>
            <div className="text-[11px] text-[var(--brand-muted)]">
              {notifications.length === 0
                ? "Noch keine Aufrufe"
                : `${notifications.length} Aktivität(en)`}
            </div>
          </div>
        </div>
        <div className="max-h-[360px] overflow-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-[var(--brand-muted)]">
              Sobald ein Kunde dein Angebot über den Tracking-Link öffnet,
              erscheint hier ein Hinweis.
            </div>
          ) : (
            notifications.map((n) => (
              <button
                type="button"
                key={n.id}
                onClick={async () => {
                  if (n.quote_id) {
                    await loadOne(n.quote_id);
                    setTab("editor");
                  }
                  try {
                    await axios.post(`${API}/notifications/${n.id}/read`);
                  } catch (_) {
                    /* notification mark read is best-effort */
                  }
                  await fetchNotifications();
                }}
                className={`w-full text-left px-4 py-3 border-b border-[var(--brand-border-light)] flex items-start gap-3 hover:bg-[var(--brand-bg)] transition cursor-pointer ${
                  n.read ? "" : "bg-[var(--brand-bg)]"
                }`}
                data-testid={`notif-${n.id}`}
              >
                {n.event === "signed" ? (
                  <CheckCircle2 className="w-4 h-4 text-[var(--brand-primary)] mt-0.5" />
                ) : n.event === "crm_task" ? (
                  <AlarmClock className="w-4 h-4 text-[var(--brand-primary)] mt-0.5" />
                ) : (
                  <Eye className="w-4 h-4 text-[var(--brand-primary)] mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--brand-ink)]">
                    {n.event === "crm_task" ? (
                      <span>{n.message}</span>
                    ) : (
                    <>
                    <span className="font-semibold">{n.kunde_anzeige}</span>{" "}
                    {n.event === "signed" ? (
                      <>
                        hat {n.doc_label || "Angebot"}{" "}
                        <span className="font-mono">{n.angebot_nr}</span>{" "}
                        <span className="font-semibold text-[var(--brand-primary)]">
                          angenommen
                        </span>
                      </>
                    ) : (
                      <>
                        hat {n.doc_label || "Angebot"}{" "}
                        <span className="font-mono">{n.angebot_nr}</span>{" "}
                        geöffnet
                      </>
                    )}
                    </>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--brand-muted)]">
                    {fmtDateTime(n.opened_at)}
                    {n.event !== "signed" && n.event !== "crm_task" && ` · ${n.view_no}. Aufruf`}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
